/**
 * Hook para signaling WebRTC via Supabase Realtime
 * Gerencia troca de SDP e ICE candidates entre peers
 * 
 * @author Sistema
 * @version 1.0.0
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import { createLogger } from '@/lib/logger';
import { supabase } from '@/integrations/supabase/client';
import type { RealtimeChannel } from '@supabase/supabase-js';
import type {
  UseWebRTCSignalingReturn,
  SignalingEventHandlers,
  SignalingPayload,
  WebRTCRole,
  RequestPayload,
  AcceptPayload,
  RejectPayload,
  OfferPayload,
  AnswerPayload,
  IceCandidatePayload,
  EndPayload,
} from './types';

// Logger para diagnóstico
const logger = createLogger('useWebRTCSignaling');

/**
 * Props do hook useWebRTCSignaling
 */
interface UseWebRTCSignalingProps {
  /** ID da conversa (usado como identificador do canal) */
  conversationId: string | null;
  /** Handlers para eventos recebidos */
  handlers?: SignalingEventHandlers;
  /** Se true, ativa a conexão com o canal */
  enabled?: boolean;
}

/**
 * Hook para signaling WebRTC via Supabase Realtime Broadcast
 * 
 * Responsabilidades:
 * - Conectar ao canal de signaling (screenshare:{conversationId})
 * - Enviar mensagens de signaling (request, accept, offer, answer, ice, end)
 * - Receber e processar mensagens do peer
 * - Gerenciar estado da conexão
 * 
 * Usa o mesmo padrão de useContactPresence.ts e useWebchatConnection.ts
 * 
 * @param props Configuração do hook
 * @returns Objeto com estado e funções de envio
 */
export function useWebRTCSignaling(props: UseWebRTCSignalingProps): UseWebRTCSignalingReturn {
  const { conversationId, handlers, enabled = true } = props;
  
  // Estado da conexão com o canal
  const [isConnected, setIsConnected] = useState(false);
  
  // Refs para canal e handlers
  const channelRef = useRef<RealtimeChannel | null>(null);
  const handlersRef = useRef(handlers);

  // Atualiza ref de handlers quando muda
  useEffect(() => {
    handlersRef.current = handlers;
  }, [handlers]);

  /**
   * Processa mensagem de signaling recebida
   */
  const handleSignalingMessage = useCallback((payload: SignalingPayload) => {
    logger.debug('Mensagem de signaling recebida', { type: payload.type });

    switch (payload.type) {
      case 'request':
        handlersRef.current?.onRequest?.(payload as RequestPayload);
        break;
      case 'accept':
        handlersRef.current?.onAccept?.(payload as AcceptPayload);
        break;
      case 'reject':
        handlersRef.current?.onReject?.(payload as RejectPayload);
        break;
      case 'offer':
        handlersRef.current?.onOffer?.(payload as OfferPayload);
        break;
      case 'answer':
        handlersRef.current?.onAnswer?.(payload as AnswerPayload);
        break;
      case 'ice':
        handlersRef.current?.onIce?.(payload as IceCandidatePayload);
        break;
      case 'end':
        handlersRef.current?.onEnd?.(payload as EndPayload);
        break;
      default:
        logger.warn('Tipo de mensagem desconhecido', { payload });
    }
  }, []);

  /**
   * Envia mensagem via broadcast
   */
  const sendMessage = useCallback((payload: SignalingPayload) => {
    const channel = channelRef.current;
    
    if (!channel) {
      logger.warn('Tentativa de enviar sem canal ativo');
      return;
    }

    logger.debug('Enviando mensagem de signaling', { type: payload.type });
    
    channel.send({
      type: 'broadcast',
      event: 'webrtc',
      payload,
    });
  }, []);

  // ============================================================================
  // FUNÇÕES DE ENVIO DE MENSAGENS
  // ============================================================================

  /**
   * Envia solicitação de visualização (Atendente → Cliente)
   */
  const sendRequest = useCallback((agentName: string, agentAvatar?: string) => {
    const payload: RequestPayload = {
      type: 'request',
      from: 'agent',
      agentName,
      agentAvatar,
      timestamp: Date.now(),
    };
    sendMessage(payload);
  }, [sendMessage]);

  /**
   * Envia aceitação da solicitação (Cliente → Atendente)
   */
  const sendAccept = useCallback(() => {
    const payload: AcceptPayload = {
      type: 'accept',
      from: 'client',
      timestamp: Date.now(),
    };
    sendMessage(payload);
  }, [sendMessage]);

  /**
   * Envia rejeição da solicitação (Cliente → Atendente)
   */
  const sendReject = useCallback((reason?: string) => {
    const payload: RejectPayload = {
      type: 'reject',
      from: 'client',
      reason,
      timestamp: Date.now(),
    };
    sendMessage(payload);
  }, [sendMessage]);

  /**
   * Envia offer SDP (Atendente → Cliente)
   */
  const sendOffer = useCallback((sdp: RTCSessionDescriptionInit) => {
    const payload: OfferPayload = {
      type: 'offer',
      from: 'agent',
      sdp,
      timestamp: Date.now(),
    };
    sendMessage(payload);
  }, [sendMessage]);

  /**
   * Envia answer SDP (Cliente → Atendente)
   */
  const sendAnswer = useCallback((sdp: RTCSessionDescriptionInit) => {
    const payload: AnswerPayload = {
      type: 'answer',
      from: 'client',
      sdp,
      timestamp: Date.now(),
    };
    sendMessage(payload);
  }, [sendMessage]);

  /**
   * Envia candidato ICE (Bidirecional)
   */
  const sendIceCandidate = useCallback((candidate: RTCIceCandidateInit, from: WebRTCRole) => {
    const payload: IceCandidatePayload = {
      type: 'ice',
      from,
      candidate,
      timestamp: Date.now(),
    };
    sendMessage(payload);
  }, [sendMessage]);

  /**
   * Envia evento de encerramento (Bidirecional)
   */
  const sendEnd = useCallback((from: WebRTCRole, reason?: EndPayload['reason']) => {
    const payload: EndPayload = {
      type: 'end',
      from,
      reason,
      timestamp: Date.now(),
    };
    sendMessage(payload);
  }, [sendMessage]);

  // ============================================================================
  // GERENCIAMENTO DO CANAL
  // ============================================================================

  /**
   * Conecta ao canal de signaling
   */
  useEffect(() => {
    // Não conecta se desabilitado ou sem conversationId
    if (!enabled || !conversationId) {
      return;
    }

    const channelName = `screenshare:${conversationId}`;
    logger.info('Conectando ao canal de signaling', { channelName });

    const channel = supabase.channel(channelName, {
      config: {
        broadcast: { self: false }, // Não recebe próprias mensagens
      },
    });

    // Listener para mensagens de signaling
    channel.on('broadcast', { event: 'webrtc' }, ({ payload }) => {
      handleSignalingMessage(payload as SignalingPayload);
    });

    // Inscreve no canal
    channel.subscribe((status) => {
      logger.debug('Status do canal', { status });
      
      if (status === 'SUBSCRIBED') {
        setIsConnected(true);
        logger.info('Conectado ao canal de signaling');
      } else if (status === 'CLOSED' || status === 'CHANNEL_ERROR') {
        setIsConnected(false);
        logger.warn('Canal de signaling desconectado', { status });
      }
    });

    channelRef.current = channel;

    // Cleanup ao desmontar ou quando conversationId muda
    return () => {
      logger.debug('Desconectando do canal de signaling');
      channel.unsubscribe();
      channelRef.current = null;
      setIsConnected(false);
    };
  }, [conversationId, enabled, handleSignalingMessage]);

  return {
    isConnected,
    sendRequest,
    sendAccept,
    sendReject,
    sendOffer,
    sendAnswer,
    sendIceCandidate,
    sendEnd,
  };
}
